import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import SearchPage from '../components/pages/SearchPage';
import LoginPromptBottomSheet from '../components/dialogs/LoginPromptBottomSheet';
import { useUserWatchlistData } from '../contexts/UserWatchlistDataContext';
import { useUserProfileCache } from '../contexts/UserProfileCacheContext';
import { isFirebaseInitialized } from '@watchlist/shared';
import { Box } from '@mui/material';
import ActionLoader from '../components/widgets/ActionLoader';
import FloatingLoginBar from '../components/widgets/FloatingLoginBar';
import { Navigate } from 'react-router-dom';
import { UserProfile } from '@watchlist/shared';
import { wasOpenedViaDeepLink, clearDeepLinkOpenedFlag } from '../services/deepLinkService';
import { getCachedUserProfile, batchFetchProfiles, getProfileFromCache } from '../utils/profileCache';
import { watchlistService } from '../services/watchlistService';
import { movieService } from '@watchlist/shared';
import UserDataManager from '../components/pages/UserDataManager';
import DataDeletionPublicPage from '../components/pages/DataDeletionPublicPage';

// LazyWatchlistApp / DashboardPage tree decommissioned — Home is now CrewRoute.
const LazyWatchlistDetailWrapper = lazy(() => import('../components/pages/WatchlistDetailWrapper'));
const LazyProfilePage = lazy(() => import('../components/pages/ProfilePage'));
const LazyMovieDetail = lazy(() => import('../components/pages/MovieDetail'));
const LazySettingsScreen = lazy(() => import('../components/pages/SettingsScreen'));
const LazyOtherUserProfile = lazy(() => import('../components/pages/OtherUserProfile'));
const LazyUsersList = lazy(() => import('../components/pages/UsersList'));
const LazyNotificationsPage = lazy(() => import('../components/pages/NotificationsPage'));
const LazyPeopleDetail = lazy(() => import('../components/pages/PeopleDetail'));
const LazyPersonCredits = lazy(() => import('../components/pages/PersonCredits'));
const LazyTrendingPage = lazy(() => import('../components/pages/TrendingPage'));
const LazyTrendingExplorePage = lazy(() => import('../components/pages/TrendingExplorePage'));
const LazyAllReviewsPage = lazy(() => import('../components/pages/AllReviewsPage'));
const LazyUserAllReviewsPage = lazy(() => import('../components/pages/UserAllReviewsPage'));
const LazyRecentlyAddedPage = lazy(() => import('../components/pages/RecentlyAddedPage'));
const LazyUserWatchedPage = lazy(() => import('../components/pages/UserWatchedPage'));
const LazyUserWantToWatchPage = lazy(() => import('../components/pages/UserWantToWatchPage'));
const LazyOtherUserWatchlistsPage = lazy(() => import('../components/pages/OtherUserWatchlistsPage'));
const LazyFavoriteStarsPage = lazy(() => import('../components/pages/FavoriteStarsPage'));
const LazyCrewPage = lazy(() => import('../components/pages/CrewPage'));
const LazyFindCrewPage = lazy(() => import('components/pages/FindCrewPage'));
const LazyHiddenFromCrewPage = lazy(() => import('../components/pages/HiddenFromCrewPage'));
const LazyStoryViewer = lazy(() => import('../components/pages/StoryViewer'));
// DEPRECATED: Using MarkAsWatchedDialog instead of a dedicated page
// const LazyMarkAsWatchedPage = lazy(() => import('../components/pages/MarkAsWatchedPage'));

// Recently Added Movies Page
export const RecentlyAddedPageRoute: React.FC = () => {
  const { user, isLoading } = useAuth();
  if (isLoading) return <ActionLoader />;
  return (
    <Suspense fallback={<ActionLoader />}>
      <LazyRecentlyAddedPage user={user || undefined} />
    </Suspense>
  );
};

/**
 * Review Detail Route - Handles deep links from shared reviews
 * URL format: /review/:reviewId
 * Redirects to movie page with highlightReview parameter
 */
export const ReviewDetailRoute: React.FC = () => {
  const { reviewId } = useParams<{ reviewId: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processReviewLink = async () => {
      if (!reviewId) {
        navigate('/', { replace: true });
        return;
      }

      try {
        console.log(`🎬 ReviewDetailRoute: Processing review ID: ${reviewId}`);
        
        const review = await movieService.getShareableReview(reviewId);
        
        if (review && review.movieId) {
          console.log(`✅ ReviewDetailRoute: Found review for movie ${review.movieId}. Redirecting...`);
          // Use /open/movie/* so Hosting serves the SPA ( /movie/* is rewritten to movieShare and loops for browsers )
          navigate(
            `/open/movie/${encodeURIComponent(review.movieId)}?highlightReview=${encodeURIComponent(reviewId)}`,
            { replace: true }
          );
        } else {
          console.warn(`❌ ReviewDetailRoute: Review not found or data incomplete`);
          setError('Review not found or it may be private');
          // Fallback to home after a short delay
          setTimeout(() => navigate('/', { replace: true }), 3000);
        }
      } catch (err) {
        console.error('Error processing review link:', err);
        setError('Something went wrong. Redirecting home...');
        setTimeout(() => navigate('/', { replace: true }), 3000);
      }
    };

    processReviewLink();
  }, [reviewId, navigate]);

  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 3,
      textAlign: 'center',
      bgcolor: 'background.default',
      color: 'text.primary'
    }}>
      {error ? (
        <>
          <Box sx={{ fontSize: '3rem', mb: 2 }}>⚠️</Box>
          <Box sx={{ fontSize: '1.2rem', fontWeight: 500, mb: 1 }}>{error}</Box>
          <Box sx={{ fontSize: '0.9rem', opacity: 0.7 }}>Returning to home...</Box>
        </>
      ) : (
        <ActionLoader />
      )}
    </Box>
  );
};

// DashboardRoute removed — Home is now CrewRoute.

// Search page wrapper
export const SearchPageRoute: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQuery = searchParams.get('q') || '';
  const initialTab = 1;

  const handleMovieSelect = (movieId: string) => {
    navigate(`/movie/${movieId}`);
  };

  const handleWatchlistSelect = (watchlistId: string, tabIndex?: number, userId?: string) => {
    const params = new URLSearchParams();
    if (tabIndex) params.set('tab', tabIndex.toString());
    if (userId) params.set('userId', userId);
    const queryString = params.toString();
    navigate(`/watchlist/${watchlistId}${queryString ? `?${queryString}` : ''}`);
  };

  const handleUserProfileSelect = (userId: string) => {
    navigate(`/profile/${userId}`);
  };

  const handlePersonSelect = (personId: string) => {
    navigate(`/person/${personId}`);
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleSearchStateChange = (query: string, tab: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (query) next.set('q', query); else next.delete('q');
      return next;
    }, { replace: true });
  };

  return (
    <SearchPage
      user={user || undefined}
      onMovieSelect={handleMovieSelect}
      onWatchlistSelect={handleWatchlistSelect}
      onUserProfileSelect={handleUserProfileSelect}
      onPersonSelect={handlePersonSelect}
      onBack={handleBack}
      initialQuery={initialQuery}
      initialTab={initialTab}
      onSearchStateChange={handleSearchStateChange}
    />
  );
};

// Explore page wrapper - redirects to search page which now includes explore functionality
export const ExplorePageRoute: React.FC = () => {
  const navigate = useNavigate();

  // Redirect to search page since explore functionality is now integrated there
  React.useEffect(() => {
    navigate('/search', { replace: true });
  }, [navigate]);

  return null;
};

// Watchlist detail wrapper with sharing support
export const WatchlistDetailRoute: React.FC = () => {
  const { watchlistId } = useParams<{ watchlistId: string }>();
  const [searchParams] = useSearchParams();
  const { user, isLoading } = useAuth();
  const { getUserDisplayName, preloadProfiles } = useUserProfileCache();
  const navigate = useNavigate();

  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [watchlistData, setWatchlistData] = useState<any>(null);
  const [loadingContent, setLoadingContent] = useState(true);

  const tabIndex = parseInt(searchParams.get('tab') || '0', 10);
  // CRITICAL FIX: Prioritize ownerId (actual owner) over sharedBy (sharing user) for proper permissions
  const targetUserId = searchParams.get('userId') || searchParams.get('ownerId') || searchParams.get('sharedBy');
  const isOtherUserWatchlist = !!targetUserId && targetUserId !== user?.uid;
  const isSharedLink = searchParams.get('shared') === 'true';

  // Enhanced fallback function for collaborator names
  const getCollaboratorFallbackName = (uid: string): string => {
    // Try to get from UserProfileCache first
    const cachedName = getUserDisplayName(uid);
    if (cachedName && !cachedName.startsWith('User ')) {
      // Using cached collaborator name
      return cachedName;
    }

    // Check if this is the sharedBy user (the person who shared the link)
    const sharedByUid = searchParams.get('sharedBy');
    if (uid === sharedByUid) {
      return 'Shared by User';
    }

    // Final fallback
    return `User ${uid.slice(-4)}`;
  };

  // Helper function to load watchlist directly by ID (works for public watchlists)
  const loadWatchlistDirectly = async (watchlistId: string) => {
    try {
      // Loading watchlist directly
      const { getDoc, doc } = await import('firebase/firestore');
      const { getDb } = await import('../services/firebase');

      const db = await getDb();
      const watchlistRef = doc(db, 'watchlists', watchlistId);
      const watchlistDoc = await getDoc(watchlistRef);

      if (watchlistDoc.exists()) {
        const watchlistData = watchlistDoc.data() as any;
        // Direct watchlist loaded successfully

        // Format the data correctly
        return {
          id: watchlistDoc.id,
          name: watchlistData.name,
          description: watchlistData.description,
          isPrivate: watchlistData.isPrivate,
          createdBy: watchlistData.createdBy || '',
          collaborators: watchlistData.collaborators || [],
          followerCount: watchlistData.followerCount || 0,
          movieDetails: watchlistData.movieDetails || [],
          updatedAt: watchlistData.updatedAt,
          createdAt: watchlistData.createdAt
        };
      }

      return null;
    } catch (error) {
      console.error('Error loading watchlist directly:', error);
      return null;
    }
  };

  // Helper function to get user profile info
  const getUserProfile = async (userId: string) => {
    try {
      // Check if Firebase is initialized before attempting to load user profile
      if (!isFirebaseInitialized()) {
        console.warn('RouteComponents: Firebase not yet initialized, returning fallback user profile');
        return { name: 'User', avatar: undefined };
      }

      const profile = await getCachedUserProfile(userId);
      return {
        name: profile?.displayName || profile?.email?.split('@')[0] || 'User',
        avatar: profile?.photoURL
      };
    } catch (error) {
      console.warn('Could not fetch user profile:', error);
      return { name: 'User', avatar: undefined };
    }
  };

  // Load watchlist data to check accessibility
  useEffect(() => {
    const loadWatchlistData = async () => {
      if (!watchlistId) return;

      // Local-only watchlists handled entirely by their own components — no Firestore lookup needed
      const LOCAL_WATCHLIST_IDS = ['instant-watchlist', 'watched', 'main-watchlist', 'want-to-watch', 'your-reviews'];
      if (LOCAL_WATCHLIST_IDS.includes(watchlistId) || watchlistId.startsWith('personality-') || watchlistId.startsWith('genre-')) {
        setLoadingContent(false);
        return;
      }

      try {
        setLoadingContent(true);

        // For shared links, try loading directly first (works for public watchlists)
        if (isSharedLink) {
          // Attempting direct load for shared watchlist
          const directWatchlist = await loadWatchlistDirectly(watchlistId);

          if (directWatchlist && (!directWatchlist.isPrivate ||
            (user && directWatchlist.collaborators?.includes(user.uid)) ||
            directWatchlist.createdBy === user?.uid)) {
            // Watchlist is accessible: either public, user is a collaborator, or user is the owner
            // Watchlist is accessible

            // Get creator profile
            const creatorProfile = await getUserProfile(directWatchlist.createdBy);

            // Resolve collaborator profiles
            // OPTIMIZATION: Batch preload all collaborator profiles first
            const collaboratorUids = directWatchlist.collaborators || [];
            if (collaboratorUids.length > 0) {
              console.log(`🔍 RouteComponents: Batch preloading ${collaboratorUids.length} collaborator profiles`);
              await batchFetchProfiles(collaboratorUids);
            }

            // Now map from cache (profiles were just batch loaded)
            const collaboratorProfiles = collaboratorUids.map((uid: string) => {
              const profile = getProfileFromCache(uid);

              if (profile) {
                console.log(`✅ RouteComponents: Got cached collaborator profile for ${uid}`);

                // Use username if available, fallback to displayName, then email, then UID
                const name = profile.username ||
                  profile.displayName ||
                  profile.email?.split('@')[0] ||
                  `User ${uid.slice(-4)}`;

                return {
                  uid,
                  name,
                  avatar: profile?.photoURL,
                  email: profile?.email
                };
              } else {
                console.warn(`❌ RouteComponents: No cached profile for collaborator: ${uid}`);

                // Use fallback logic
                let fallbackName = getCollaboratorFallbackName(uid);
                const sharedByUid = searchParams.get('sharedBy');
                if (uid === sharedByUid) {
                  fallbackName = 'Shared by User';
                }

                return {
                  uid,
                  name: fallbackName,
                  avatar: undefined,
                  email: undefined
                };
              }
            });

            setWatchlistData({
              ...directWatchlist,
              createdByName: creatorProfile.name,
              createdByAvatar: creatorProfile.avatar,
              collaboratorProfiles,
              movieCount: directWatchlist.movieDetails?.length || 0
            });

            setLoadingContent(false);
            return;
          }

          // If direct loading failed or watchlist is private, try loading from user
          if (targetUserId) {
            console.log('Direct loading failed or private, trying user-specific loading...');
            try {
              const userWatchlists = await watchlistService.getUserWatchlists(targetUserId);
              const sharedWatchlist = userWatchlists.find(w => w.id === watchlistId);

              if (sharedWatchlist && (!sharedWatchlist.isPrivate ||
                (user && sharedWatchlist.collaborators?.includes(user.uid)) ||
                sharedWatchlist.createdBy === user?.uid)) {
                console.log('Watchlist found and accessible:', sharedWatchlist.name);

                // Get creator profile
                const creatorProfile = await getUserProfile(sharedWatchlist.createdBy || targetUserId);

                // Resolve collaborator profiles - OPTIMIZATION: Batch preload first
                const sharedCollaboratorUids = sharedWatchlist.collaborators || [];
                if (sharedCollaboratorUids.length > 0) {
                  console.log(`🔍 RouteComponents: Batch preloading ${sharedCollaboratorUids.length} collaborator profiles`);
                  await batchFetchProfiles(sharedCollaboratorUids);
                }

                const collaboratorProfiles = sharedCollaboratorUids.map((uid: string) => {
                  const profile = getProfileFromCache(uid);

                  if (profile) {
                    const name = profile.username ||
                      profile.displayName ||
                      profile.email?.split('@')[0] ||
                      `User ${uid.slice(-4)}`;

                    return {
                      uid,
                      name,
                      avatar: profile?.photoURL,
                      email: profile?.email
                    };
                  } else {
                    let fallbackName = `User ${uid.slice(-4)}`;
                    const sharedByUid = searchParams.get('sharedBy');
                    if (uid === sharedByUid) {
                      fallbackName = 'Shared by User';
                    }

                    return {
                      uid,
                      name: fallbackName,
                      avatar: undefined,
                      email: undefined
                    };
                  }
                });

                setWatchlistData({
                  name: sharedWatchlist.name,
                  description: sharedWatchlist.description,
                  isPrivate: sharedWatchlist.isPrivate,
                  createdBy: sharedWatchlist.createdBy || '',
                  createdByName: creatorProfile.name,
                  createdByAvatar: creatorProfile.avatar,
                  collaborators: sharedWatchlist.collaborators || [],
                  collaboratorProfiles,
                  followerCount: sharedWatchlist.followerCount || 0,
                  movieCount: sharedWatchlist.movieDetails?.length || 0,
                  movieDetails: sharedWatchlist.movieDetails || [],
                  updatedAt: sharedWatchlist.updatedAt,
                  createdAt: sharedWatchlist.createdAt
                });
              } else {
                // Check if watchlist exists but user doesn't have access
                const foundWatchlist = userWatchlists.find(w => w.id === watchlistId);
                if (foundWatchlist && foundWatchlist.isPrivate &&
                  !foundWatchlist.collaborators?.includes(user?.uid || '') &&
                  foundWatchlist.createdBy !== user?.uid) {
                  console.warn('Watchlist found but user is not a collaborator');
                  setWatchlistData({
                    name: 'Private Watchlist',
                    description: 'This watchlist is private. Ask the owner to add you as a collaborator to view it.',
                    isPrivate: true,
                    createdBy: '',
                    createdByName: 'User',
                    collaborators: [],
                    collaboratorProfiles: [],
                    followerCount: 0,
                    movieCount: 0,
                    movieDetails: []
                  });
                } else {
                  console.warn('Watchlist not found in user\'s data');
                  setWatchlistData({
                    name: 'Watchlist not found',
                    description: 'This watchlist could not be found.',
                    isPrivate: true,
                    createdBy: '',
                    createdByName: 'User',
                    collaborators: [],
                    collaboratorProfiles: [],
                    followerCount: 0,
                    movieCount: 0,
                    movieDetails: []
                  });
                }
              }
            } catch (error) {
              console.error('Error loading from user data:', error);
              setWatchlistData({
                name: 'Watchlist not found or is private',
                description: 'Unable to load this watchlist. It may be private or inaccessible.',
                isPrivate: true,
                createdBy: '',
                createdByName: 'User',
                collaborators: [],
                collaboratorProfiles: [],
                followerCount: 0,
                movieCount: 0,
                movieDetails: []
              });
            }
          } else {
            console.warn('No targetUserId for private/not found watchlist');
            setWatchlistData({
              name: 'Watchlist not found or is private',
              description: 'This watchlist could not be found.',
              isPrivate: true,
              createdBy: '',
              createdByName: 'User',
              collaborators: [],
              collaboratorProfiles: [],
              followerCount: 0,
              movieCount: 0,
              movieDetails: []
            });
          }
        } else if (user) {
          // Normal logged-in user flow
          if (targetUserId && targetUserId !== user.uid) {
            // Load other user's watchlist
            console.log('Loading other user watchlist from:', targetUserId);
            try {
              const otherUserWatchlists = await watchlistService.getUserWatchlists(targetUserId);
              const otherWatchlist = otherUserWatchlists.find(w => w.id === watchlistId);

              if (otherWatchlist && (!otherWatchlist.isPrivate ||
                (user && otherWatchlist.collaborators?.includes(user.uid)) ||
                otherWatchlist.createdBy === user?.uid)) {
                // Get creator profile
                const creatorProfile = await getUserProfile(otherWatchlist.createdBy || targetUserId);

                // Resolve collaborator profiles - OPTIMIZATION: Batch preload first
                const otherCollaboratorUids = otherWatchlist.collaborators || [];
                if (otherCollaboratorUids.length > 0) {
                  console.log(`🔍 RouteComponents: Batch preloading ${otherCollaboratorUids.length} collaborator profiles`);
                  await batchFetchProfiles(otherCollaboratorUids);
                }

                const collaboratorProfiles = otherCollaboratorUids.map((uid: string) => {
                  const profile = getProfileFromCache(uid);

                  if (profile) {
                    const name = profile.username ||
                      profile.displayName ||
                      profile.email?.split('@')[0] ||
                      `User ${uid.slice(-4)}`;

                    return {
                      uid,
                      name,
                      avatar: profile.photoURL,
                      email: profile.email
                    };
                  } else {
                    return {
                      uid,
                      name: `User ${uid.slice(-4)}`,
                      avatar: undefined,
                      email: undefined
                    };
                  }
                });

                setWatchlistData({
                  name: otherWatchlist.name,
                  description: otherWatchlist.description,
                  isPrivate: otherWatchlist.isPrivate,
                  createdBy: otherWatchlist.createdBy || '',
                  createdByName: creatorProfile.name,
                  createdByAvatar: creatorProfile.avatar,
                  collaborators: otherWatchlist.collaborators || [],
                  collaboratorProfiles,
                  followerCount: otherWatchlist.followerCount || 0,
                  movieCount: otherWatchlist.movieDetails?.length || 0,
                  movieDetails: otherWatchlist.movieDetails || [],
                  updatedAt: otherWatchlist.updatedAt,
                  createdAt: otherWatchlist.createdAt
                });
              } else {
                // Check if watchlist exists but user doesn't have access
                const foundOtherWatchlist = otherUserWatchlists.find(w => w.id === watchlistId);
                if (foundOtherWatchlist && foundOtherWatchlist.isPrivate &&
                  !foundOtherWatchlist.collaborators?.includes(user?.uid || '') &&
                  foundOtherWatchlist.createdBy !== user?.uid) {
                  console.warn('Other user watchlist found but user is not a collaborator');
                  setWatchlistData({
                    name: 'Private Watchlist',
                    description: 'This watchlist is private. Ask the owner to add you as a collaborator to view it.',
                    isPrivate: true,
                    createdBy: '',
                    createdByName: 'User',
                    collaborators: [],
                    collaboratorProfiles: [],
                    followerCount: 0,
                    movieCount: 0,
                    movieDetails: []
                  });
                } else {
                  console.warn('Other user watchlist not found');
                  setWatchlistData({
                    name: 'Watchlist Not Found',
                    description: 'This watchlist could not be found',
                    isPrivate: true,
                    createdBy: '',
                    createdByName: 'User',
                    collaborators: [],
                    collaboratorProfiles: [],
                    followerCount: 0,
                    movieCount: 0,
                    movieDetails: []
                  });
                }
              }
            } catch (error) {
              console.error('Error loading other user watchlist:', error);
              setWatchlistData({
                name: 'Error Loading Watchlist',
                description: 'Unable to load this watchlist',
                isPrivate: true,
                createdBy: '',
                createdByName: 'User',
                collaborators: [],
                collaboratorProfiles: [],
                followerCount: 0,
                movieCount: 0,
                movieDetails: []
              });
            }
          } else {
            // Normal current user watchlist loading
            const userWatchlists = await watchlistService.getUserWatchlists(user.uid);
            const userWatchlist = userWatchlists.find(w => w.id === watchlistId);

            if (userWatchlist) {
              // Get creator profile
              const creatorProfile = await getUserProfile(userWatchlist.createdBy || user.uid);

              // Resolve collaborator profiles
              console.log('🔍 RouteComponents: Loading collaborator profiles for current user watchlist:', {
                watchlistId: userWatchlist.id,
                collaboratorCount: userWatchlist.collaborators?.length || 0,
                collaborators: userWatchlist.collaborators,
                currentUserId: user.uid
              });

              const collaboratorProfiles = await Promise.all(
                (userWatchlist.collaborators || []).map(async (uid: string) => {
                  try {
                    console.log(`🔍 RouteComponents: Fetching profile for collaborator: ${uid}`);
                    const profile = await getCachedUserProfile(uid);

                    if (profile) {
                      console.log('✅ RouteComponents: Successfully loaded collaborator profile:', {
                        uid,
                        displayName: profile.displayName,
                        email: profile.email,
                        username: profile.username
                      });

                      // Use username if available, fallback to displayName, then email, then UID
                      const name = profile.username ||
                        profile.displayName ||
                        profile.email?.split('@')[0] ||
                        `User ${uid.slice(-4)}`;

                      return {
                        uid,
                        name,
                        avatar: profile?.photoURL,
                        email: profile?.email
                      };
                    } else {
                      console.warn('❌ RouteComponents: No profile document found for collaborator:', uid);

                      // 🚀 ENHANCED FALLBACK: Try to get collaborator info from the watchlist data itself
                      let fallbackName = `User ${uid.slice(-4)}`;
                      const sharedByUid = searchParams.get('sharedBy');
                      if (uid === sharedByUid) {
                        fallbackName = 'Shared by User';
                        console.log(`💡 RouteComponents: Using 'Shared by User' for sharedBy collaborator: ${uid}`);
                      }

                      return {
                        uid,
                        name: fallbackName,
                        avatar: undefined,
                        email: undefined
                      };
                    }
                  } catch (error) {
                    console.error('💥 RouteComponents: Failed to get collaborator profile for:', uid, error);

                    // 🚀 ENHANCED FALLBACK: Same logic for error cases
                    let fallbackName = `User ${uid.slice(-4)}`;
                    const sharedByUid = searchParams.get('sharedBy');
                    if (uid === sharedByUid) {
                      fallbackName = 'Shared by User';
                      console.log(`💡 RouteComponents: Using 'Shared by User' for sharedBy collaborator (error case): ${uid}`);
                    }

                    return {
                      uid,
                      name: fallbackName,
                      avatar: undefined,
                      email: undefined
                    };
                  }
                })
              );

              setWatchlistData({
                name: userWatchlist.name,
                description: userWatchlist.description,
                isPrivate: userWatchlist.isPrivate,
                createdBy: userWatchlist.createdBy || '',
                createdByName: creatorProfile.name,
                createdByAvatar: creatorProfile.avatar,
                collaborators: userWatchlist.collaborators || [],
                collaboratorProfiles,
                followerCount: userWatchlist.followerCount || 0,
                movieCount: userWatchlist.movieDetails?.length || 0,
                movieDetails: userWatchlist.movieDetails || [],
                updatedAt: userWatchlist.updatedAt,
                createdAt: userWatchlist.createdAt
              });
            } else {
              console.warn('User watchlist not found');
              setWatchlistData({
                name: 'Watchlist Not Found',
                description: 'This watchlist could not be found',
                isPrivate: true,
                createdBy: '',
                createdByName: 'User',
                collaborators: [],
                collaboratorProfiles: [],
                followerCount: 0,
                movieCount: 0,
                movieDetails: []
              });
            }
          }
        } else {
          // User not logged in - try to load public watchlist directly
          console.log('User not logged in, attempting to load public watchlist...');
          const directWatchlist = await loadWatchlistDirectly(watchlistId);

          if (directWatchlist && !directWatchlist.isPrivate) {
            // Public watchlist found!
            console.log('Public watchlist loaded for non-logged-in user:', directWatchlist.name);

            // Get creator profile
            const creatorProfile = await getUserProfile(directWatchlist.createdBy);

            // CRITICAL FIX: Load collaborator profiles for shared public watchlists
            console.log('Loading collaborator profiles for public watchlist:', directWatchlist.collaborators?.length || 0, 'collaborators');
            const collaboratorProfiles = await Promise.all(
              (directWatchlist.collaborators || []).map(async (uid: string) => {
                try {
                  const profile = await getCachedUserProfile(uid);
                  if (profile) {
                    console.log('✅ Loaded collaborator profile for public watchlist:', uid, '→', profile.displayName);
                    return {
                      uid,
                      name: profile.displayName || profile.email?.split('@')[0] || `User ${uid.slice(-4)}`,
                      avatar: profile.photoURL,
                      email: profile.email
                    };
                  } else {
                    console.warn('❌ No profile document found for public watchlist collaborator:', uid);

                    // 🚀 ENHANCED FALLBACK: Try to get collaborator info from the watchlist data itself
                    let fallbackName = `User ${uid.slice(-4)}`;
                    const sharedByUid = searchParams.get('sharedBy');
                    if (uid === sharedByUid) {
                      fallbackName = 'Shared by User';
                      console.log(`💡 RouteComponents: Using 'Shared by User' for sharedBy collaborator: ${uid}`);
                    }

                    return {
                      uid,
                      name: fallbackName,
                      avatar: undefined,
                      email: undefined
                    };
                  }
                } catch (error) {
                  console.warn('💥 Failed to get collaborator profile for public watchlist:', uid, error);

                  // 🚀 ENHANCED FALLBACK: Same logic for error cases
                  let fallbackName = `User ${uid.slice(-4)}`;
                  const sharedByUid = searchParams.get('sharedBy');
                  if (uid === sharedByUid) {
                    fallbackName = 'Shared by User';
                    console.log(`💡 RouteComponents: Using 'Shared by User' for sharedBy collaborator (error case): ${uid}`);
                  }

                  return {
                    uid,
                    name: fallbackName,
                    avatar: undefined,
                    email: undefined
                  };
                }
              })
            );

            setWatchlistData({
              ...directWatchlist,
              createdByName: creatorProfile.name,
              createdByAvatar: creatorProfile.avatar,
              collaboratorProfiles,
              movieCount: directWatchlist.movieDetails?.length || 0
            });
          } else {
            // Private watchlist or not found - show login prompt
            console.log('Private watchlist or not found, showing login prompt');
            setShowLoginPrompt(true);
          }
        }

      } catch (error) {
        console.error('Error in loadWatchlistData:', error);
        setWatchlistData({
          name: 'Error Loading Watchlist',
          description: 'An error occurred while loading this watchlist',
          isPrivate: true,
          createdBy: '',
          createdByName: 'User',
          collaborators: [],
          collaboratorProfiles: [],
          followerCount: 0,
          movieCount: 0,
          movieDetails: []
        });
      } finally {
        setLoadingContent(false);
      }
    };

    if (!isLoading) {
      loadWatchlistData();
    }
  }, [watchlistId, user, targetUserId, isSharedLink, isLoading]);

  const handleBack = () => {
    // Personality watchlists: a specific person/genre list was always pushed
    // from the "all" category list, so a single history.back() is correct.
    // (The old code forced navigate('/') for `id==='all'` to avoid a scroll-
    // restoration bug that's since been fixed by the per-tab scroll containers.)
    if (watchlistId?.startsWith('personality-')) {
      window.history.back();
      return;
    }

    // Deep link / shared cold-open: replace navigation leaves no real stack to pop — go home.
    if (wasOpenedViaDeepLink() || window.history.length <= 1) {
      clearDeepLinkOpenedFlag();
      navigate('/');
      return;
    }

    // Everything else (custom + default watchlists): pop history. Default
    // watchlists used to force navigate('/') to avoid a scroll-restoration
    // bug — that bug is gone now that scrolls live in per-tab containers, so
    // honoring history is correct and lands the user back wherever they
    // opened the list from (profile, search, crew, etc.).
    window.history.back();
  };

  const handleMovieSelect = (movieId: string, tabIndex?: number) => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    const url = `/movie/${movieId}${tabIndex ? `?tab=${tabIndex}` : ''}`;
    navigate(url);
  };

  const handleLikesSelect = (listId: string) => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    navigate(`/users?type=likes&watchlistId=${listId}&title=Likes`);
  };

  const handleUserProfileSelect = (userId: string) => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    navigate(`/profile/${userId}`);
  };

  const handlePersonSelect = (personId: string) => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    navigate(`/person/${personId}`);
  };

  const handleLogin = () => {
    navigate('/');
  };

  if (isLoading || loadingContent) {
    return <ActionLoader />;
  }

  return (
    <>
      <Suspense fallback={<ActionLoader />}>
        <LazyWatchlistDetailWrapper
          onBack={handleBack}
          onMovieSelect={handleMovieSelect}
          onLikesSelect={handleLikesSelect}
          onUserProfileSelect={handleUserProfileSelect}
          onPersonSelect={handlePersonSelect}
          watchlistType={watchlistId || ''}
          initialTab={tabIndex}
          user={user || undefined}
          targetUserId={targetUserId || undefined}
          isOtherUserWatchlist={isOtherUserWatchlist}
          isSharedLink={isSharedLink}
          externalWatchlistData={watchlistData}
        />
      </Suspense>

      {/* Only show login prompt if it's not a shared link */}
      {!isSharedLink && (
        <LoginPromptBottomSheet
          open={showLoginPrompt}
          onClose={() => setShowLoginPrompt(false)}
          onLogin={handleLogin}
          shareType="watchlist"
          itemTitle={watchlistData?.name || 'Watchlist'}
          sharedBy={targetUserId ? 'Someone' : undefined}
        />
      )}
    </>
  );
};

// Movie detail wrapper with sharing support
export const MovieDetailRoute: React.FC = () => {
  const { movieId } = useParams<{ movieId: string }>();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [movieData, setMovieData] = useState<any>(null);

  const movieTitle = searchParams.get('title');
  const sharedRating = searchParams.get('rating');
  const sharedReview = searchParams.get('review');
  const tabIndex = searchParams.get('tab') ? parseInt(searchParams.get('tab')!) : undefined;
  const isOpenMoviePublicShell = pathname.startsWith('/open/movie/');
  // `highlightReview` is an in-app deep link (scroll/highlight), not a public "share card" — do not force shared chrome.
  // `/open/movie/*` is the SPA entry path for Hosting; only logged-out visitors get the logo / public share shell.
  const hasShareQueryContent =
    searchParams.get('shared') === 'true' || !!(sharedRating || sharedReview);
  const isSharedLink = hasShareQueryContent || (!user && isOpenMoviePublicShell);

  // Load movie data and handle sharing
  useEffect(() => {
    const loadMovieData = async () => {
      if (!movieId) return;

      try {
        // For movies, we can always show the basic info even to non-logged users
        // The TMDB data is public
        setMovieData({
          id: movieId,
          title: movieTitle || 'Movie',
          sharedRating,
          sharedReview: sharedReview ? decodeURIComponent(sharedReview) : undefined,
        });
      } catch (error) {
        console.error('Error loading movie data:', error);
      }
    };

    if (!isLoading) {
      loadMovieData();
    }
  }, [movieId, movieTitle, isLoading, sharedRating, sharedReview]);

  const handleBack = () => {
    // Deep link / shared link cold-open: replace navigation means no real stack to pop.
    if (wasOpenedViaDeepLink() || window.history.length <= 1) {
      clearDeepLinkOpenedFlag();
      navigate('/');
      return;
    }
    window.history.back();
  };

  const handlePersonSelect = (personId: string) => {
    navigate(`/person/${personId}`);
  };

  const handleUserSelect = (userId: string) => {
    navigate(`/profile/${userId}`);
  };

  const handleLogin = () => {
    navigate('/');
  };

  if (isLoading) {
    return <ActionLoader />;
  }

  return (
    <>
      <Suspense fallback={<ActionLoader />}>
        <LazyMovieDetail
          onBack={handleBack}
          movieId={movieId || ''}
          movieTitle={movieTitle || undefined}
          onPersonSelect={handlePersonSelect}
          onUserSelect={handleUserSelect}
          user={user || undefined}
          isSharedLink={isSharedLink}
          tabIndex={tabIndex}
        />
      </Suspense>

      {!user && isSharedLink && (
        <FloatingLoginBar
          onLogin={handleLogin}
          shareType="movie"
          itemTitle={movieData?.title}
          bottomInsetVariant="floating"
        />
      )}

      {/* Only show login prompt if it's not a shared link */}
      {!isSharedLink && (
        <LoginPromptBottomSheet
          open={showLoginPrompt}
          onClose={() => setShowLoginPrompt(false)}
          onLogin={handleLogin}
          shareType="movie"
          itemTitle={movieData?.title || 'Movie'}
          sharedBy={undefined}
        />
      )}
    </>
  );
};

// DEPRECATED: Mark as Watched Page - Using MarkAsWatchedDialog instead
// export const MarkAsWatchedPageRoute: React.FC = () => {
//   return (
//     <Suspense fallback={<ActionLoader />}>
//       <LazyMarkAsWatchedPage />
//     </Suspense>
//   );
// };

// Profile page wrapper
export const ProfilePageRoute: React.FC = () => {
  const { user } = useAuth();
  const { refreshData } = useUserWatchlistData();
  const navigate = useNavigate();

  const handleMovieSelect = (movieId: string) => {
    navigate(`/movie/${movieId}`);
  };

  const handleWatchlistSelect = (watchlistId: string) => {
    navigate(`/watchlist/${watchlistId}`);
  };

  const handleUserListSelect = (type: 'followers' | 'following' | 'likes', title: string) => {
    navigate(`/users?type=${type}&title=${encodeURIComponent(title)}&userId=${user?.uid}`);
  };



  const handleUserProfileSelect = (userId: string) => {
    navigate(`/profile/${userId}`);
  };

  const handlePersonSelect = (personId: string) => {
    navigate(`/person/${personId}`);
  };

  if (!user) return null;

  return (
    <Suspense fallback={<ActionLoader />}>
      <LazyProfilePage
        user={user}
        onMovieSelect={handleMovieSelect}
        onWatchlistSelect={handleWatchlistSelect}
        onUserListSelect={handleUserListSelect}

        firebaseUser={user}
        onUserProfileSelect={handleUserProfileSelect}
        onWatchlistDataRefresh={refreshData}
        onPersonSelect={handlePersonSelect}
      />
    </Suspense>
  );
};

// Users list page wrapper
export const UsersListRoute: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const listType = (searchParams.get('type') || 'followers') as 'followers' | 'following' | 'likes';
  const title = searchParams.get('title') || 'Users';
  const targetUserId = searchParams.get('userId') || user?.uid;
  const watchlistId = searchParams.get('watchlistId'); // For specific watchlist likes

  const handleBack = () => {
    window.history.back();
  };

  const handleUserSelect = (userId: string) => {
    // Push the profile onto the stack — back from the profile will pop back to this /users page
    // via window.history.back(), avoiding the returnTo loop bug.
    navigate(`/profile/${userId}`);
  };

  return (
    <Suspense fallback={<ActionLoader />}>
      <LazyUsersList
        onBack={handleBack}
        onUserSelect={handleUserSelect}
        title={title}
        currentUserId={user?.uid}
        listType={listType}
        targetUserId={targetUserId}
        watchlistId={watchlistId || undefined} // Pass watchlistId for specific watchlist likes
      />
    </Suspense>
  );
};

// Other user profile wrapper with sharing support
export const OtherUserProfileRoute: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);

  const isSharedLink = searchParams.get('shared') === 'true';

  // Load profile data and handle sharing
  useEffect(() => {
    const loadProfileData = async () => {
      if (!userId) return;

      try {
        // Load basic profile data
        const profile = await getCachedUserProfile(userId);

        if (profile) {
          setProfileData(profile);

          // Show login prompt for shared links when user is not logged in
          if (!user && isSharedLink) {
            setShowLoginPrompt(true);
          }
        }
      } catch (error) {
        console.error('Error loading profile data:', error);
        setProfileData({ displayName: 'User Profile', uid: userId });

        if (!user) {
          setShowLoginPrompt(true);
        }
      }
    };

    if (!isLoading) {
      loadProfileData();
    }
  }, [userId, user, isLoading, isSharedLink]);

  const handleBack = () => {
    const returnTo = searchParams.get('returnTo');
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    if (wasOpenedViaDeepLink() || window.history.length <= 1) {
      clearDeepLinkOpenedFlag();
      navigate('/');
      return;
    }
    window.history.back();
  };

  const handleMovieSelect = (movieId: string) => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    navigate(`/movie/${movieId}`);
  };

  const handleWatchlistSelect = (watchlistId: string, tabIndex?: number) => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    navigate(`/watchlist/${watchlistId}?tab=${tabIndex || 0}&userId=${userId}`);
  };

  const handleUserSelect = (selectedUserId: string) => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    // Pass the current profile URL as returnTo so back navigation works correctly
    const currentPath = `/profile/${userId}`;
    navigate(`/profile/${selectedUserId}?returnTo=${encodeURIComponent(currentPath)}`);
  };

  const handleLogin = () => {
    navigate('/');
  };

  if (isLoading) {
    return <ActionLoader />;
  }

  return (
    <>
      <Suspense fallback={<ActionLoader />}>
        <LazyOtherUserProfile
          userId={userId || ''}
          onBack={handleBack}
          onMovieSelect={handleMovieSelect}
          onWatchlistSelect={handleWatchlistSelect}
          onUserSelect={handleUserSelect}
          currentUser={user || undefined}
        />
      </Suspense>

      <LoginPromptBottomSheet
        open={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        onLogin={handleLogin}
        shareType="profile"
        itemTitle={profileData?.displayName || 'User Profile'}
        sharedBy={isSharedLink ? 'Someone' : undefined}
      />
    </>
  );
};

/** Data deletion URL for store listings: signed-in users get full My Data UI; others see public instructions. */
export const DataDeletionRoute: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  if (isLoading) {
    return <ActionLoader />;
  }

  if (user) {
    return (
      <UserDataManager
        user={user}
        onBack={() => {
          if (typeof window !== 'undefined' && window.history.length > 1) {
            navigate(-1);
          } else {
            navigate('/settings');
          }
        }}
      />
    );
  }

  return <DataDeletionPublicPage />;
};

// Settings screen wrapper
export const SettingsScreenRoute: React.FC = () => {
  const { user } = useAuth();
  const { toggleTheme } = useTheme();

  const handleBack = () => {
    window.history.back();
  };

  if (!user) return null;

  return (
    <Suspense fallback={<ActionLoader />}>
      <LazySettingsScreen
        onBack={handleBack}
        user={user}
        onThemeToggle={toggleTheme}
      />
    </Suspense>
  );
};

// Notifications page wrapper
export const NotificationsRoute: React.FC = () => {
  const { user, isLoading } = useAuth();

  // Show loading spinner while authentication is being checked
  // This prevents the splash screen flash when navigating to notifications
  if (isLoading) {
    return <ActionLoader />;
  }

  // If no user is authenticated, redirect to home
  if (!user) {
    return <Navigate to="/" replace />;
  }

  return (
    <Suspense fallback={<ActionLoader />}>
      <LazyNotificationsPage />
    </Suspense>
  );
};

// Crew feed page wrapper
export const CrewRoute: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  if (isLoading) {
    return <ActionLoader />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return (
    <Suspense fallback={<ActionLoader />}>
      <LazyCrewPage
        user={user}
        onMovieSelect={movieId => navigate(`/movie/${movieId}`)}
        onUserSelect={userId => navigate(`/profile/${userId}`)}
        onWatchlistSelect={watchlistId => navigate(`/watchlist/${watchlistId}`)}
        onReviewSelect={reviewId => navigate(`/review/${encodeURIComponent(reviewId)}`)}
        onPersonSelect={personId => navigate(`/person/${encodeURIComponent(personId)}`)}
      />
    </Suspense>
  );
};

export const FindCrewRoute: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  if (isLoading) {
    return <ActionLoader />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return (
    <Suspense fallback={<ActionLoader />}>
      <LazyFindCrewPage
        user={user}
        onBack={() => navigate('/')}
        onUserSelect={userId => navigate(`/profile/${userId}`)}
      />
    </Suspense>
  );
};

export const HiddenFromCrewRoute: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  if (isLoading) {
    return <ActionLoader />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return (
    <Suspense fallback={<ActionLoader />}>
      <LazyHiddenFromCrewPage
        user={user}
        onBack={() => navigate('/')}
      />
    </Suspense>
  );
};

// Person detail page wrapper
export const PersonDetailRoute: React.FC = () => {
  const { personId } = useParams<{ personId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleBack = () => {
    if (wasOpenedViaDeepLink() || window.history.length <= 1) {
      clearDeepLinkOpenedFlag();
      navigate('/');
      return;
    }
    window.history.back();
  };

  const handleMovieSelect = (movieId: string) => {
    navigate(`/movie/${movieId}`);
  };

  const handleShowAllCredits = (personId: string, personName: string) => {
    navigate(`/person/${personId}/credits?name=${encodeURIComponent(personName)}`);
  };

  if (!personId) {
    return <Navigate to="/" replace />;
  }

  return (
    <Suspense fallback={<ActionLoader />}>
      <LazyPeopleDetail
        personId={personId}
        onBack={handleBack}
        onMovieSelect={handleMovieSelect}
        onShowAllCredits={handleShowAllCredits}
      />
    </Suspense>
  );
};

// Person credits page wrapper
export const PersonCreditsRoute: React.FC = () => {
  const { personId } = useParams<{ personId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const personName = searchParams.get('name') || 'Person';

  const handleBack = () => {
    window.history.back();
  };

  const handleMovieSelect = (movieId: string) => {
    navigate(`/movie/${movieId}`);
  };

  if (!personId) {
    return <Navigate to="/" replace />;
  }

  return (
    <Suspense fallback={<ActionLoader />}>
      <LazyPersonCredits
        personId={personId}
        personName={personName}
        onBack={handleBack}
        onMovieSelect={handleMovieSelect}
      />
    </Suspense>
  );
};

// Trending explore page (ExploreSection) — shown when /trending has no ?tab= param
export const TrendingExplorePageRoute: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <Suspense fallback={<ActionLoader />}>
      <LazyTrendingExplorePage
        user={user || undefined}
        onMovieSelect={(movieId) => navigate(`/movie/${movieId}`)}
        onWatchlistSelect={(watchlistId, tabIndex, userId) => {
          const params = new URLSearchParams();
          if (tabIndex) params.set('tab', tabIndex.toString());
          if (userId) params.set('userId', userId);
          const qs = params.toString();
          navigate(`/watchlist/${watchlistId}${qs ? `?${qs}` : ''}`);
        }}
      />
    </Suspense>
  );
};

// Trending page wrapper
export const TrendingPageRoute: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleMovieSelect = (movieId: string) => {
    navigate(`/movie/${movieId}`);
  };

  const handleWatchlistSelect = (watchlistId: string, tabIndex?: number, userId?: string) => {
    const params = new URLSearchParams();
    if (tabIndex) params.set('tab', tabIndex.toString());
    if (userId) params.set('userId', userId);
    const queryString = params.toString();
    navigate(`/watchlist/${watchlistId}${queryString ? `?${queryString}` : ''}`);
  };

  return (
    <Suspense fallback={<ActionLoader />}>
      <LazyTrendingPage
        user={user || undefined}
        onMovieSelect={handleMovieSelect}
        onWatchlistSelect={handleWatchlistSelect}
      />
    </Suspense>
  );
};

// Smart router for /trending: no ?tab param → ExploreSection, ?tab=N → TrendingPage
export const TrendingTabRoute: React.FC = () => {
  const [searchParams] = useSearchParams();
  return searchParams.has('tab') ? <TrendingPageRoute /> : <TrendingExplorePageRoute />;
};

// All reviews page wrapper
export const AllReviewsPageRoute: React.FC = () => {
  return (
    <Suspense fallback={<ActionLoader />}>
      <LazyAllReviewsPage />
    </Suspense>
  );
};

// User Reviews Page Wrapper
export const UserReviewsPageRoute: React.FC = () => {
  return (
    <Suspense fallback={<ActionLoader />}>
      <LazyUserAllReviewsPage />
    </Suspense>
  );
};

// User Watched Page
export const UserWatchedPageRoute: React.FC = () => {
  return (
    <Suspense fallback={<ActionLoader />}>
      <LazyUserWatchedPage />
    </Suspense>
  );
};

// User Want to Watch Page
export const UserWantToWatchPageRoute: React.FC = () => {
  return (
    <Suspense fallback={<ActionLoader />}>
      <LazyUserWantToWatchPage />
    </Suspense>
  );
};

// Other User Watchlists Page
export const OtherUserWatchlistsPageRoute: React.FC = () => {
  return (
    <Suspense fallback={<ActionLoader />}>
      <LazyOtherUserWatchlistsPage />
    </Suspense>
  );
};

// Favourite Stars Page
export const FavoriteStarsPageRoute: React.FC = () => {
  return (
    <Suspense fallback={<ActionLoader />}>
      <LazyFavoriteStarsPage />
    </Suspense>
  );
};

// Story Viewer
export const StoryViewerRoute: React.FC = () => {
  return (
    <Suspense fallback={null}>
      <LazyStoryViewer />
    </Suspense>
  );
};
